<script setup lang="ts">
import { computed } from 'vue'
import CanvasPreviewSurface, { type PreviewDrawItem, type PreviewHotspotItem } from '@/components/workspace/CanvasPreviewSurface.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type {
  SettingsSystemAudioDraft,
  SettingsSystemButtonDraft,
  SettingsSystemTabProps,
} from '@/features/workspace/settings/types'

const props = defineProps<SettingsSystemTabProps>()

function handleSystemButtonEnabledChange(item: SettingsSystemButtonDraft) {
  if (item.enabled && item.index === 0 && props.imageOptionPickerItems.length > 1) {
    item.index = 1
  }
  if (!item.enabled) {
    item.index = 0
  }
}

function handleSystemAudioEnabledChange(item: SettingsSystemAudioDraft) {
  item.volume = Math.max(0, Math.min(100, Number(item.volume) || 0))
}

const previewDrawItems = computed<PreviewDrawItem[]>(() => {
  const buttonItems = props.systemPreviewButtons.map((item) => ({
    key: `button-${item.key}`,
    kind: 'image' as const,
    style: item.style,
    imageUrl: item.url,
    placeholderText: item.label,
  }))
  const audioItems = props.systemPreviewAudioItems.flatMap((item) => [
    {
      key: `${item.key}-label`,
      kind: 'text' as const,
      style: item.labelStyle,
      text: item.label,
    },
    {
      key: `${item.key}-track`,
      kind: 'image' as const,
      style: item.trackStyle,
      imageUrl: props.systemBarNoneUrl,
      placeholderText: '滚动条',
    },
    {
      key: `${item.key}-thumb`,
      kind: 'image' as const,
      style: item.thumbStyle,
      imageUrl: props.systemBarMoveUrl,
      placeholderText: '滑块',
    },
    {
      key: `${item.key}-value`,
      kind: 'text' as const,
      style: item.valueStyle,
      text: item.value,
    },
  ])
  return [...buttonItems, ...audioItems]
})

const previewHotspotItems = computed<PreviewHotspotItem[]>(() =>
  props.systemPreviewButtons.map((item) => ({
    key: `button-${item.key}`,
    style: {
      ...item.style,
      outline: props.activeSystemDragIndex === item.key ? '1px dashed var(--theme-accent)' : 'none',
      cursor: 'move',
    },
    ariaLabel: `${item.label}预览拖拽热区`,
    onPointerdown: (event: PointerEvent) => props.startSystemButtonDrag(item.key, event),
  })),
)
</script>

<template>
  <div v-if="portraitLayout" class="settings-page settings-portrait-page system-page-portrait">
    <section class="setting-card settings-preview-card settings-portrait-preview system-preview-card">
      <CanvasPreviewSurface
        host-class="settings-preview-shell system-preview-shell"
        stage-class="settings-preview-stage system-preview-stage"
        :background-url="systemBackgroundUrl"
        :design-width="screenWidth"
        :design-height="screenHeight"
        background-empty-text="系统设置背景图"
        :draw-items="previewDrawItems"
        :hotspot-items="previewHotspotItems"
        :guide-active="previewGuideActive"
        @stage-resize="onPreviewStageResize"
      />
    </section>

    <div class="settings-portrait-props">
      <section class="setting-card system-resource-card">
        <div class="system-resource-grid">
          <label class="field-block system-resource-field">
            <span class="field-label">背景图</span>
            <ResourceSelectField
              :model-value="systemSetting.BackPic.name"
              category="graphic"
              :base-folder-path="backgroundGraphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择系统设置背景图"
              @update:model-value="updateSystemBackPic"
            />
          </label>

          <label class="field-block system-resource-field">
            <span class="field-label">滚动条底图</span>
            <ResourceSelectField
              :model-value="systemSetting.BarNone.name"
              category="graphic"
              :base-folder-path="uiGraphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择滚动条底图"
              @update:model-value="updateSystemBarNonePic"
            />
          </label>

          <label class="field-block system-resource-field">
            <span class="field-label">滚动条拖动图</span>
            <ResourceSelectField
              :model-value="systemSetting.BarMove.name"
              category="graphic"
              :base-folder-path="uiGraphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择滚动条拖动图"
              @update:model-value="updateSystemBarMovePic"
            />
          </label>
        </div>
      </section>

      <section class="setting-card system-button-card">
        <div class="card-title">按钮显示</div>

        <div class="settings-item-list system-button-list">
          <div
            v-for="item in systemSetting.Buttons"
            :key="`system-button-portrait-${item.key}`"
            class="settings-item-card system-button-item"
          >
            <div class="settings-item-row settings-item-row--main">
              <label class="settings-item-enable">
                <input
                  v-model="item.enabled"
                  type="checkbox"
                  @change="handleSystemButtonEnabledChange(item)"
                />
              </label>
              <div class="settings-item-name">{{ item.label }}</div>
              <ImageOptionPickerField
                :model-value="item.index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                :disabled="!item.enabled"
                title="选择系统按钮图片选项"
                @update:model-value="item.index = $event"
              />
            </div>

            <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain system-axis-row">
              <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
              <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
            </div>
          </div>
        </div>
      </section>

      <section class="setting-card">
        <div class="card-title">音乐设置</div>

        <div class="settings-item-list">
          <div
            v-for="item in systemSetting.AudioItems"
            :key="`system-audio-portrait-${item.key}`"
            class="settings-item-card system-audio-item"
          >
            <div class="settings-item-row settings-item-row--main">
              <label class="settings-item-enable">
                <input
                  v-model="item.enabled"
                  type="checkbox"
                  @change="handleSystemAudioEnabledChange(item)"
                />
              </label>
              <div class="settings-item-name">{{ item.label }}</div>
              <NumberStepperField
                v-model="item.volume"
                class="cell-input sm"
                :min="0"
                :max="100"
                :disabled="!item.enabled"
              />
            </div>

            <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain system-axis-row">
              <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
              <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <div v-else class="settings-page system-page">
    <div class="system-grid">
      <div class="system-left-column">
        <section class="setting-card system-button-card">
          <div class="card-title">按钮显示</div>

          <div class="settings-item-list system-button-list">
            <div
              v-for="item in systemSetting.Buttons"
              :key="item.key"
              class="settings-item-card system-button-item"
            >
              <div class="settings-item-row settings-item-row--main">
                <label class="settings-item-enable">
                  <input
                    v-model="item.enabled"
                    type="checkbox"
                    @change="handleSystemButtonEnabledChange(item)"
                  />
                </label>
                <div class="settings-item-name">{{ item.label }}</div>
                <ImageOptionPickerField
                  :model-value="item.index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  :disabled="!item.enabled"
                  title="选择系统按钮图片选项"
                  @update:model-value="item.index = $event"
                />
              </div>

              <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain system-axis-row">
                <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
                <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
              </div>
            </div>
          </div>
        </section>

        <section class="setting-card">
          <div class="card-title">音乐设置</div>

          <div class="settings-item-list">
            <div
              v-for="item in systemSetting.AudioItems"
              :key="item.key"
              class="settings-item-card system-audio-item"
            >
              <div class="settings-item-row settings-item-row--main">
                <label class="settings-item-enable">
                  <input
                    v-model="item.enabled"
                    type="checkbox"
                    @change="handleSystemAudioEnabledChange(item)"
                  />
                </label>
                <div class="settings-item-name">{{ item.label }}</div>
                <NumberStepperField
                  v-model="item.volume"
                  class="cell-input sm"
                  :min="0"
                  :max="100"
                  :disabled="!item.enabled"
                />
              </div>

              <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain system-axis-row">
                <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
                <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="system-right-column">
        <section class="setting-card system-resource-card">
          <div class="system-resource-grid">
            <label class="field-block system-resource-field">
              <span class="field-label">背景图</span>
              <ResourceSelectField
                :model-value="systemSetting.BackPic.name"
                category="graphic"
                :base-folder-path="backgroundGraphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择系统设置背景图"
                @update:model-value="updateSystemBackPic"
              />
            </label>

            <label class="field-block system-resource-field">
              <span class="field-label">滚动条底图</span>
              <ResourceSelectField
                :model-value="systemSetting.BarNone.name"
                category="graphic"
                :base-folder-path="uiGraphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择滚动条底图"
                @update:model-value="updateSystemBarNonePic"
              />
            </label>

            <label class="field-block system-resource-field">
              <span class="field-label">滚动条拖动图</span>
              <ResourceSelectField
                :model-value="systemSetting.BarMove.name"
                category="graphic"
                :base-folder-path="uiGraphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择滚动条拖动图"
                @update:model-value="updateSystemBarMovePic"
              />
            </label>
          </div>
        </section>

        <section class="setting-card settings-preview-card system-preview-card">
          <CanvasPreviewSurface
            host-class="settings-preview-shell system-preview-shell"
            stage-class="settings-preview-stage system-preview-stage"
            :background-url="systemBackgroundUrl"
            :design-width="screenWidth"
            :design-height="screenHeight"
            background-empty-text="系统设置背景图"
            :draw-items="previewDrawItems"
            :hotspot-items="previewHotspotItems"
            :guide-active="previewGuideActive"
            @stage-resize="onPreviewStageResize"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.system-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
}

.system-left-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow: hidden;
}

.system-right-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.system-resource-card {
  flex-shrink: 0;
}

.system-resource-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}

.system-button-card {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.system-button-list {
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.system-page-portrait .system-button-card {
  flex: 0 0 auto;
  overflow: visible;
}

.system-page-portrait .settings-item-list {
  flex: 0 0 auto;
  overflow: visible;
}

.system-resource-field :deep(.resource-select-input),
.system-button-item :deep(.image-option-picker-input) {
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  border: 1px solid var(--theme-border);
}

.system-resource-field :deep(.resource-select-trigger),
.system-button-item :deep(.image-option-picker-trigger) {
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
}

.system-preview-button,
.system-preview-audio-label,
.system-preview-audio-track,
.system-preview-audio-thumb,
.system-preview-audio-value {
  position: absolute;
  z-index: 1;
}

.system-preview-button {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: move;
  user-select: none;
  touch-action: none;
  box-sizing: border-box;
}

.system-preview-button img,
.system-preview-audio-track img,
.system-preview-audio-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}

.system-preview-button--dragging {
  border: 1px dashed var(--theme-accent);
}

.system-preview-audio-label,
.system-preview-audio-value {
  font-size: 12px;
  line-height: 1.2;
  color: var(--theme-text-heading);
}

.system-preview-audio-track,
.system-preview-audio-thumb {
  overflow: hidden;
}

.system-axis-row {
  padding-left: 0;
}

@media (max-width: 1180px) and (orientation: landscape) {
  .system-grid {
    grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  }

  .system-resource-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1200px) and (orientation: portrait) {
  .system-grid {
    grid-template-columns: 1fr;
  }

  .system-resource-grid {
    grid-template-columns: 1fr;
  }
}
</style>
